import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { getSafeAssetUrl } from '../../utils/helpers';

export const DebugTables: React.FC = () => {
  const {
    lineItems,
    equipmentItems,
    pvWireItems,
    updateLineItem,
    highlightItem,
    screenshots,
  } = useApp();

  const [isOpen, setIsOpen] = useState(false);

  const hasData =
    lineItems.length > 0 || equipmentItems.length > 0 || pvWireItems.length > 0;

  if (!hasData) {
    return null;
  }

  // Extract non-underscore headers
  const getHeaders = (items: Record<string, any>[]) => {
    return Array.from(new Set(items.flatMap((item) => Object.keys(item)))).filter(
      (h) => !h.startsWith('_')
    );
  };

  const lineHeaders = getHeaders(lineItems);
  const equipHeaders = getHeaders(equipmentItems);
  const pvHeaders = getHeaders(pvWireItems);

  const screenshotEntries = [
    { label: 'Wire Schedule', url: screenshots.wireScheduleUrl },
    { label: 'Equipment Schedule', url: screenshots.equipmentScheduleUrl },
    ...(screenshots.stringLayoutUrls || []).map((url, i) => ({
      label: `String Layout ${i + 1}`,
      url,
    })),
  ]
    .map((entry) => ({
      ...entry,
      url: entry.url ? getSafeAssetUrl(entry.url) : null,
    }))
    .filter((entry) => entry.url);

  return (
    <div style={{ marginTop: '24px' }}>
      <button
        type="button"
        className="btn-action"
        style={{ width: '100%', justifyContent: 'center' }}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        {isOpen ? (
          <>
            <EyeOff className="h-4 w-4" />
            <span>Hide Raw Debug Tables</span>
          </>
        ) : (
          <>
            <Eye className="h-4 w-4" />
            <span>View Raw Data &amp; Screenshots</span>
          </>
        )}
      </button>

      {isOpen && (
        <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Wire Schedule Table */}
          {lineItems.length > 0 && (
            <div>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '1rem', fontWeight: 600 }}>
                Wire Schedule
              </h3>
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      {lineHeaders.map((header) => (
                        <th key={header}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {lineItems.map((item, rowIdx) => (
                      <tr
                        key={rowIdx}
                        style={{ cursor: 'pointer' }}
                        onClick={() => highlightItem(item['_bbox'], rowIdx)}
                      >
                        {lineHeaders.map((header) => (
                          <td key={header}>
                            <input
                              type="text"
                              value={item[header] ?? ''}
                              onChange={(e) => updateLineItem(rowIdx, header, e.target.value)}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Equipment Schedule Table */}
          {equipmentItems.length > 0 && (
            <div>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '1rem', fontWeight: 600 }}>
                Equipment Schedule
              </h3>
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      {equipHeaders.map((header) => (
                        <th key={header}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {equipmentItems.map((item, rowIdx) => (
                      <tr
                        key={rowIdx}
                        style={{ cursor: 'pointer' }}
                        onClick={() => highlightItem(item['_bbox'])}
                      >
                        {equipHeaders.map((header) => (
                          <td key={header}>{item[header] ?? ''}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* PV Wire Schedule Table */}
          {pvWireItems.length > 0 && (
            <div>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '1rem', fontWeight: 600 }}>
                PV String Layout
              </h3>
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      {pvHeaders.map((header) => (
                        <th key={header}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pvWireItems.map((item, rowIdx) => (
                      <tr
                        key={rowIdx}
                        style={{ cursor: 'pointer' }}
                        onClick={() => highlightItem(item['_bbox'])}
                      >
                        {pvHeaders.map((header) => (
                          <td key={header}>{item[header] ?? ''}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Screenshots View */}
          {screenshotEntries.length > 0 && (
            <div>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '1rem', fontWeight: 600 }}>
                AI Processing Screenshots
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {screenshotEntries.map(({ label, url }) => (
                  <div key={label}>
                    <h4 style={{ margin: '0 0 6px 0', fontSize: '0.9rem', color: 'var(--text-muted)' }}>
                      {label}
                    </h4>
                    <img
                      src={url!}
                      alt={label}
                      style={{
                        maxWidth: '100%',
                        borderRadius: '8px',
                        border: '1px solid var(--border-color)',
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
